// Read-only mode and stale notes (TASK-579, ADR-104): when the API is unreachable, the proxy
// admin sees a bannered note on every screen, and every write control is disabled. The context
// defaults to read-write (readOnly: false), so the billing admin is unaffected.
import { createContext } from "preact";
import { useContext } from "preact/hooks";

export interface ReadOnly {
  /** Whether all write controls should be disabled. */
  readOnly: boolean;
  /** A human-readable reason to show as a title when disabled, or null if read-write. */
  reason: string | null;
}

const DEFAULT_READ_ONLY: ReadOnly = {
  readOnly: false,
  reason: null,
};

export const ReadOnlyContext = createContext<ReadOnly>(DEFAULT_READ_ONLY);

/**
 * The read-only state from the nearest provider, or the default (read-write).
 * Use this on every write control: when readOnly is true, render the control disabled with
 * the reason as its title attribute and an aria-describedby pointing at a stale-note on
 * the screen.
 */
export function useReadOnly(): ReadOnly {
  return useContext(ReadOnlyContext);
}

export interface ReadOnlyProviderProps {
  readOnly: boolean;
  reason: string | null;
  children: preact.ComponentChildren;
}

/**
 * Provides the read-only state to all child components.
 * Used at the root of proxy-admin-area screens when the API is unreachable.
 * On the billing admin, wrap with readOnly: false, reason: null, or omit the provider
 * to use the default.
 */
export function ReadOnlyProvider({ readOnly, reason, children }: ReadOnlyProviderProps) {
  return (
    <ReadOnlyContext.Provider value={{ readOnly, reason }}>
      {children}
    </ReadOnlyContext.Provider>
  );
}
